import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2, Save } from "lucide-react";
import * as React from "react";

import { type AdminMarkupResponse, getAdminMarkup, updateAdminMarkup } from "@ando/core";
import { Button, PencilCard, cn, useToast } from "@ando/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * GlobalMarkupCard — the markup % editor, reused for the GLOBAL default and for
 * a PER-TENANT override (pass ``tenantId``). One component, one wire contract
 * (``getAdminMarkup``/``updateAdminMarkup`` with optional tenant scope) → the
 * Billing hub and the Org → Billing tab share it (DRY).
 */

const FALLBACK_MIN = 0;
const FALLBACK_MAX = 500;

function parseMarkup(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export interface GlobalMarkupCardProps {
  /** ``undefined`` = global default; a tenant id = that tenant's override. */
  tenantId?: string;
  className?: string;
}

export function GlobalMarkupCard({
  tenantId,
  className,
}: GlobalMarkupCardProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();
  const scope = tenantId ?? "global";
  const QK = ["admin", "pricing", "markup", scope] as const;

  const q = useQuery({
    queryKey: QK,
    queryFn: () => getAdminMarkup(tenantId),
    staleTime: 60_000,
  });

  const min = q.data?.min_pct ?? FALLBACK_MIN;
  const max = q.data?.max_pct ?? FALLBACK_MAX;
  const serverValue = q.data?.markup_pct ?? null;

  const [draft, setDraft] = React.useState("");
  React.useEffect(() => {
    if (serverValue !== null) setDraft(String(serverValue));
  }, [serverValue]);

  const parsed = parseMarkup(draft);
  const outOfRange = parsed !== null && (parsed < min || parsed > max);
  const valid = parsed !== null && !outOfRange;
  const dirty = valid && parsed !== serverValue;

  const save = useMutation({
    mutationFn: async () => {
      if (parsed === null) throw new Error("no value");
      return updateAdminMarkup(parsed, tenantId);
    },
    onSuccess: (res: AdminMarkupResponse) => {
      qc.setQueryData(QK, res);
      toast.success(i18n._(msg`Markup salvato: ${res.markup_pct}%`));
    },
    onError: (err) =>
      toast.error(i18n._(msg`Salvataggio markup fallito`), {
        description: err instanceof Error ? err.message : undefined,
      }),
  });

  return (
    <PencilCard className={cn("max-w-md p-5", className)}>
      {q.isLoading ? (
        <div className="h-9 w-40 animate-pulse rounded-md bg-ink-1/5" aria-hidden />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-[11px] text-ink-3 uppercase tracking-wide">
              <Trans>Markup sul costo LLM</Trans>
            </span>
            <div className="relative w-40">
              <input
                type="number"
                inputMode="decimal"
                min={min}
                max={max}
                step={0.5}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                aria-label={
                  tenantId
                    ? i18n._(msg`Override markup del tenant`)
                    : i18n._(msg`Percentuale di markup globale`)
                }
                aria-invalid={!valid && draft.trim() !== ""}
                className={cn(
                  "h-9 w-full rounded-md border bg-card pr-8 pl-2.5 text-right text-[13px] text-ink-1 tabular-nums",
                  "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  valid || draft.trim() === "" ? "border-hairline" : "border-destructive",
                )}
              />
              <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[13px] text-ink-3">
                %
              </span>
            </div>
          </div>

          {outOfRange ? (
            <p className="text-[11.5px] text-destructive">
              <Trans>
                Il markup deve essere tra {min}% e {max}%.
              </Trans>
            </p>
          ) : (
            <p className="text-[11.5px] text-ink-3">
              {tenantId ? (
                <Trans>Override per questo tenant — sostituisce il markup globale.</Trans>
              ) : (
                <Trans>Vale per tutti i tenant salvo override per-tenant.</Trans>
              )}
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={() => valid && !save.isPending && save.mutate()}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              <Trans>Salva markup</Trans>
            </Button>
            {dirty ? (
              <span className="text-[11px] text-ink-3">
                <Trans>Modifiche non salvate</Trans>
              </span>
            ) : null}
          </div>
        </div>
      )}
    </PencilCard>
  );
}
